// اجزای مشترک صفحات درخواست خرید کالا/خدمات و بلیط هواپیما
import React, { useState, useRef } from 'react';
import { ui } from '../../components/ListPage';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import WarningDialog from '../../components/WarningDialog';
import { openReport } from '../../components/report';
import { jsonOf, netErr } from '../../utils/http';

export const API = (path, opts = {}) => fetch(`/api/tadarokat/requests${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

export const qs = o => Object.entries(o || {})
  .filter(([, v]) => v !== undefined && v !== null && v !== '')
  .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

export const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
export const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
  .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
export const num = v => { const n = Number(toEng(String(v ?? '')).replace(/,/g, '')); return isNaN(n) ? 0 : n; };
export const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');
export const splitLines = m => String(m || '').split('\n');

export const curYear = () => {
  const y = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/')[0];
  return parseInt(y, 10) || 1405;
};

/** ستون‌هایی که در همهٔ ردیف‌ها خالی‌اند نشان داده نمی‌شوند */
export const visibleCols = (cols, rows) => (rows && rows.length ? cols.filter(c => c.always || rows.some(r => {
  const v = r[c.key];
  return v !== '' && v !== null && v !== undefined && v !== 0 && v !== '0';
})) : cols);

/** اجرای گزارش؛ پیام خطا با onError */
export async function runReport(key, params, onError) {
  try {
    await openReport(key, params);
  } catch (e) {
    if (onError) onError(e.message || 'خطا در تهیه گزارش');
  }
}

/** پوستهٔ تمام‌صفحه برای فرم‌هایی که از فرم اصلی باز می‌شوند */
export function SubFormShell({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: '#f0f2f7', overflow: 'hidden' }}>
      {children}
    </div>
  );
}

/** پیام یک‌دکمه‌ای */
export function MsgBox({ lines, onClose, title = 'پیام' }) {
  if (!lines || !lines.length) return null;
  return <WarningDialog title={title} lines={lines} confirmLabel="تایید" cancelLabel="بستن"
    onConfirm={onClose} onCancel={onClose} />;
}

/** پرسش بلی/خیر */
export function AskBox({ title = 'هشدار', lines, onYes, onNo, danger }) {
  return <WarningDialog title={title} lines={lines} confirmLabel="بلی" cancelLabel="خیر" tone={danger ? 'danger' : 'warn'}
    onConfirm={onYes} onCancel={onNo} />;
}

/** برچسب + ورودی */
export function Field({ label, value, onChange, flex = 1, disabled, onKeyDown, onFocus, onBlur, ltr, minWidth = '110px', hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, minWidth }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value ?? ''} onChange={e => onChange && onChange(e.target.value)} disabled={disabled}
        onKeyDown={onKeyDown} onFocus={onFocus} onBlur={onBlur} title={hint}
        style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff', direction: ltr ? 'ltr' : 'rtl' }} />
    </div>
  );
}

export function DateField({ label, value, onChange, disabled, minWidth = '130px' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth }}>
      <label style={ui.mlabel}>{label}</label>
      <JalaliDatePicker value={value || ''} onChange={onChange} placeholder={label} disabled={disabled} />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, disabled, flex = 1 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, minWidth: '140px' }}>
      <label style={ui.mlabel}>{label}</label>
      <select value={value} disabled={disabled} onChange={e => onChange(Number(e.target.value))}
        style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff' }}>
        {options.map((o, i) => <option key={i} value={i}>{o}</option>)}
      </select>
    </div>
  );
}

export function Box({ title, children }) {
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 10px', flex: 1, minWidth: '220px' }}>
      <div style={ui.flabel}>{title}</div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

/**
 * جستجوی انتخابی — سرور {id, code, title, display} برمی‌گرداند.
 * onLeave(text, found): خروج از فیلد بدون انتخاب (برای افزودن مورد تازه).
 */
export function Lookup({ label, value, endpoint, onPick, onLeave, flex = 1, disabled, minWidth = '160px' }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const picked = useRef(false);
  const search = async (text) => {
    setQ(text);
    picked.current = false;
    const j = await API(`${endpoint}${endpoint.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  const pick = r => { picked.current = true; onPick(r); setOpen(false); setQ(''); };
  const blur = () => setTimeout(() => {
    setOpen(false);
    if (!picked.current && q.trim() && onLeave) onLeave(q.trim(), rows.length);
  }, 200);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative', minWidth }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} disabled={disabled} placeholder="جستجو..."
        onChange={e => { if (!disabled) { if (value) onPick({ id: 0, title: '', display: '' }); search(e.target.value); } }}
        onBlur={blur}
        style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff' }} />
      {value && !disabled && (
        <button type="button" onClick={() => { onPick({ id: 0, title: '', display: '' }); setQ(''); }}
          style={{ position: 'absolute', left: '6px', bottom: '7px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}>✕</button>
      )}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 1700, maxHeight: '260px', overflowY: 'auto' }}>
          {rows.map(r => (
            <div key={r.id} onMouseDown={() => pick(r)}
              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e8f0fe'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
              {toFa(r.display || r.title)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** جدول؛ ستون‌ها: {key, label, money?, num?, wrap?} — checked: مجموعهٔ شناسه‌های انتخاب‌شده (حالت چندانتخابی) */
export function Grid({ columns, rows, selectedId, onSelect, onDouble, checked, onCheck, empty = 'رکوردی یافت نشد', loading, zebra = '#fdf6ec' }) {
  return (
    <>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <tr>
            {checked && <th style={{ ...ui.th, width: '30px' }}>✓</th>}
            {columns.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const sel = selectedId != null && selectedId === r.id;
            const bg = sel ? '#e8f0fe' : (i % 2 ? zebra : '#fff');
            return (
              <tr key={r.id ?? i} style={{ background: bg, cursor: 'pointer' }}
                onClick={() => (checked ? onCheck && onCheck(r) : onSelect && onSelect(r))}
                onDoubleClick={() => onDouble && onDouble(r)}>
                {checked && <td style={ui.td}><input type="checkbox" readOnly checked={checked.has(r.id)} /></td>}
                {columns.map(c => {
                  const v = r[c.key];
                  const t = c.money ? fmtAmt(v) : (c.num ? (Number(v || 0) ? Number(v).toLocaleString('fa-IR') : '•') : toFa(v ?? ''));
                  return (
                    <td key={c.key} title={String(v ?? '')}
                      style={{ ...(c.money || c.num ? ui.tdNum : ui.td), textAlign: c.money ? 'left' : (c.wrap ? 'right' : 'center'),
                        maxWidth: c.wrap ? '280px' : undefined, whiteSpace: c.wrap ? 'pre-wrap' : undefined,
                        fontWeight: sel ? 700 : undefined }}>
                      {t}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!loading && rows.length === 0 && <div style={ui.empty}>{empty}</div>}
    </>
  );
}

/** پنجرهٔ مودال */
export function Modal({ title, onClose, width = '900px', footer, children, z }) {
  return (
    <div style={{ ...ui.overlay, ...(z ? { zIndex: z } : {}) }} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: width, overflow: 'visible' }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={{ ...ui.mBody, overflow: 'visible' }}>{children}</div>
        {footer && <div style={ui.mFooter}>{footer}</div>}
      </div>
    </div>
  );
}

export const tbtn = (style, on = true) => ({ ...style, cursor: on ? 'pointer' : 'default', opacity: on ? 1 : 0.4 });
